import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { tendersAPI } from '../api/api';
import { toast } from '../hooks/use-toast';
import AppLayout from '../components/AppLayout';
import {
  Upload, FileText, Sparkles, CheckCircle2, ArrowRight, RefreshCw,
  AlertCircle, X, Briefcase, Globe, Calendar, Building2, Plus, Trash2, ClipboardPaste,
} from 'lucide-react';

// ───────────────────────────────────────────────────────────────────────────
// NewTender — paste-or-upload → AI extract → review → save
//
// Mirrors the NewDossier flow visually for consistency.
// ───────────────────────────────────────────────────────────────────────────

const PHASE = { IDLE: 'idle', PROCESSING: 'processing', REVIEW: 'review', ERROR: 'error' };
const TAB = { UPLOAD: 'upload', PASTE: 'paste' };

const PROGRESS_TAU_MS = 30_000;
const PROGRESS_CAP = 0.95;
const easedProgress = (elapsedMs) =>
  Math.min(PROGRESS_CAP, 1 - Math.exp(-elapsedMs / PROGRESS_TAU_MS));

const NewTender = () => {
  const navigate = useNavigate();

  const [tab, setTab] = useState(TAB.UPLOAD);
  const [pasteText, setPasteText] = useState('');
  const [phase, setPhase] = useState(PHASE.IDLE);
  const [statusMsg, setStatusMsg] = useState('');
  const [progress, setProgress] = useState(0);
  const [parsed, setParsed] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const fileInputRef = useRef(null);
  const startRef = useRef(0);
  const progressTimerRef = useRef(null);
  const cancelledRef = useRef(false);

  useEffect(() => () => {
    cancelledRef.current = true;
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
  }, []);

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) processFile(f);
  };

  const startProgress = () => {
    setProgress(0);
    startRef.current = Date.now();
    progressTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - startRef.current;
      setProgress(easedProgress(elapsed) * 100);
    }, 200);
  };

  const finishAndShowReview = async (result) => {
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    setProgress(100);
    await new Promise(r => setTimeout(r, 800));
    if (cancelledRef.current) return;
    setParsed(normalizeParsed(result));
    setPhase(PHASE.REVIEW);
  };

  const failProcessing = (err) => {
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    const msg = err?.response?.data?.detail || err?.message || "Impossible d'analyser l'AO.";
    setErrorMsg(msg);
    setPhase(PHASE.ERROR);
  };

  const processFile = async (file) => {
    if (!file.name.toLowerCase().match(/\.(pdf|doc|docx)$/)) {
      toast({ title: 'Format invalide', description: 'PDF ou Word uniquement.', variant: 'destructive' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: 'Fichier trop volumineux', description: 'Max 10 MB.', variant: 'destructive' });
      return;
    }
    setPhase(PHASE.PROCESSING);
    setStatusMsg("Envoi de l'AO…");
    setErrorMsg('');
    startProgress();
    try {
      setStatusMsg("L'IA extrait les compétences requises…");
      const result = await tendersAPI.parseFile(file);
      if (cancelledRef.current) return;
      setStatusMsg("Extraction terminée.");
      await finishAndShowReview(result);
    } catch (e) {
      if (!cancelledRef.current) failProcessing(e);
    }
  };

  const processText = async () => {
    const text = pasteText.trim();
    if (text.length < 80) {
      toast({ title: 'Texte trop court', description: 'Collez le texte intégral de l\'AO (≥ 80 caractères).', variant: 'destructive' });
      return;
    }
    setPhase(PHASE.PROCESSING);
    setStatusMsg("Analyse de l'AO…");
    setErrorMsg('');
    startProgress();
    try {
      setStatusMsg("L'IA extrait les compétences requises…");
      const result = await tendersAPI.parseText(text);
      if (cancelledRef.current) return;
      setStatusMsg("Extraction terminée.");
      await finishAndShowReview(result);
    } catch (e) {
      if (!cancelledRef.current) failProcessing(e);
    }
  };

  const handleSave = async () => {
    if (!parsed || saving) return;
    setSaving(true);
    try {
      const created = await tendersAPI.create({
        ...parsed,
        status: 'actif',
      });
      toast({ title: 'Appel d\'offre créé', description: 'Calcul des correspondances en cours…' });
      navigate(`/tenders/${created.id}`);
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
      setSaving(false);
    }
  };

  const handleRetry = () => {
    setPhase(PHASE.IDLE);
    setProgress(0);
    setErrorMsg('');
    setParsed(null);
    setPasteText('');
  };

  // ── Review-screen skill editing ────────────────────────────────────────
  const updateSkill = (i, field, value) => {
    setParsed(p => ({
      ...p,
      required_skills: p.required_skills.map((s, idx) => idx === i ? { ...s, [field]: value } : s),
    }));
  };
  const removeSkill = (i) => setParsed(p => ({ ...p, required_skills: p.required_skills.filter((_, idx) => idx !== i) }));
  const addSkill = () => setParsed(p => ({
    ...p,
    required_skills: [...(p.required_skills || []), { name: '', importance: 'must_have', category: 'Technique' }],
  }));

  const cardBase = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 14,
    padding: 22,
  };

  return (
    <AppLayout title="Nouvel appel d'offre">
      <div style={{ maxWidth: 820, margin: '20px auto 60px', padding: '0 12px' }}>

        {/* Header */}
        <div style={{ marginBottom: 22 }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--white)', letterSpacing: -0.3, marginBottom: 4 }}>
            Nouvel appel d'offre
          </div>
          <div style={{ fontSize: 13, color: 'var(--muted2)' }}>
            Importez un AO — l'IA extrait le périmètre et les compétences requises, puis calcule les correspondances avec vos consultants.
          </div>
        </div>

        {/* ── IDLE phase: tabs + input ── */}
        {phase === PHASE.IDLE && (
          <>
            {/* Tabs */}
            <div style={{
              display: 'inline-flex', gap: 4, padding: 4,
              background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 9,
              marginBottom: 18,
            }}>
              {[
                { key: TAB.UPLOAD, label: 'Importer un fichier', icon: <Upload size={13} /> },
                { key: TAB.PASTE, label: 'Coller du texte', icon: <ClipboardPaste size={13} /> },
              ].map(t => (
                <button key={t.key}
                  onClick={() => setTab(t.key)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '7px 14px', borderRadius: 6,
                    background: tab === t.key ? 'var(--surface3)' : 'transparent',
                    color: tab === t.key ? 'var(--white)' : 'var(--muted2)',
                    border: 'none', fontSize: 12.5, fontWeight: 600,
                    cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >{t.icon} {t.label}</button>
              ))}
            </div>

            {tab === TAB.UPLOAD && (
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  ...cardBase, cursor: 'pointer', padding: '52px 30px', textAlign: 'center',
                  borderStyle: 'dashed', borderWidth: 2,
                  borderColor: dragOver ? 'var(--vio)' : 'rgba(123,104,238,0.25)',
                  background: dragOver ? 'rgba(123,104,238,0.06)' : 'var(--surface)',
                  transition: 'all 0.18s ease',
                }}
              >
                <div style={{
                  width: 72, height: 72, margin: '0 auto 18px', borderRadius: 18,
                  background: 'linear-gradient(135deg, rgba(123,104,238,0.18), rgba(160,153,232,0.12))',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--vio2)',
                }}>
                  <Upload size={28} strokeWidth={1.6} />
                </div>
                <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--white)', marginBottom: 6 }}>
                  Glissez-déposez l'AO ici
                </div>
                <div style={{ fontSize: 13, color: 'var(--muted2)', marginBottom: 18 }}>
                  ou cliquez pour parcourir vos fichiers
                </div>
                <button type="button" onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '9px 18px', borderRadius: 8, border: 'none',
                    background: 'var(--vio)', color: '#fff',
                    fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                  }}>
                  <FileText size={14} /> Choisir un fichier
                </button>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 14 }}>
                  Formats acceptés : PDF, DOC, DOCX · 10 MB max
                </div>
                <input
                  ref={fileInputRef} type="file" accept=".pdf,.doc,.docx" style={{ display: 'none' }}
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) processFile(f); e.target.value = ''; }}
                />
              </div>
            )}

            {tab === TAB.PASTE && (
              <div style={cardBase}>
                <textarea
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  placeholder="Collez ici le texte intégral de l'appel d'offre (objectif, périmètre, prestations demandées, compétences requises…)"
                  style={{
                    width: '100%', minHeight: 320, resize: 'vertical',
                    background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8,
                    padding: 14, color: 'var(--white)', fontSize: 13, lineHeight: 1.55,
                    outline: 'none', fontFamily: 'inherit',
                  }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
                  <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                    {pasteText.length} caractère{pasteText.length !== 1 ? 's' : ''}
                  </span>
                  <button
                    onClick={processText}
                    disabled={pasteText.trim().length < 80}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '9px 18px', borderRadius: 8, border: 'none',
                      background: 'var(--vio)', color: '#fff',
                      fontSize: 13, fontWeight: 600,
                      cursor: pasteText.trim().length < 80 ? 'not-allowed' : 'pointer',
                      opacity: pasteText.trim().length < 80 ? 0.5 : 1,
                      fontFamily: 'inherit',
                    }}
                  >
                    <Sparkles size={13} /> Lancer l'analyse
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── PROCESSING ── */}
        {phase === PHASE.PROCESSING && (
          <div style={{ ...cardBase, padding: '40px 32px', textAlign: 'center' }}>
            <div style={{ marginBottom: 24, position: 'relative', height: 88, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div className="nd-pulse-ring" />
              <div className="nd-pulse-ring nd-pulse-ring-2" />
              <div style={{
                position: 'relative', zIndex: 2,
                width: 64, height: 64, borderRadius: 16,
                background: 'linear-gradient(135deg, var(--vio), var(--vio2))',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 8px 28px rgba(123,104,238,0.45)',
              }}>
                <Sparkles size={26} color="#fff" strokeWidth={1.8} className="nd-sparkle-rotate" />
              </div>
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--white)', marginBottom: 6 }}>
              {progress >= 100 ? 'Analyse terminée !' : "Analyse de l'AO en cours"}
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted2)', marginBottom: 24, minHeight: 18 }}>{statusMsg}</div>
            <div style={{
              height: 8, background: 'rgba(255,255,255,0.06)',
              borderRadius: 6, overflow: 'hidden', maxWidth: 420, margin: '0 auto',
            }}>
              <div style={{
                height: '100%', width: `${progress}%`,
                background: 'linear-gradient(90deg, var(--vio), var(--vio2))',
                borderRadius: 6,
                transition: progress >= 100 ? 'width 0.45s ease-out' : 'width 0.4s linear',
                boxShadow: '0 0 12px rgba(123,104,238,0.5)',
              }} />
            </div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 12, fontWeight: 600 }}>
              {Math.round(progress)} %
            </div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 24, lineHeight: 1.6 }}>
              L'analyse prend généralement entre 20 secondes et 2 minutes.
            </div>
          </div>
        )}

        {/* ── REVIEW ── */}
        {phase === PHASE.REVIEW && parsed && (
          <>
            <div className="nd-success-banner" style={{
              background: 'linear-gradient(135deg, rgba(34,197,94,0.12), rgba(34,197,94,0.04))',
              border: '1px solid rgba(34,197,94,0.3)',
              borderRadius: 14, padding: '18px 22px',
              display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18,
            }}>
              <div className="nd-check-pop" style={{
                width: 42, height: 42, borderRadius: '50%', background: '#22c55e',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                boxShadow: '0 0 24px rgba(34,197,94,0.5)',
              }}>
                <CheckCircle2 size={24} color="#fff" strokeWidth={2.2} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--white)', marginBottom: 2 }}>
                  AO analysé avec succès
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted2)' }}>
                  Vérifiez/corrigez les champs ci-dessous puis enregistrez. Le matching se calcule automatiquement.
                </div>
              </div>
            </div>

            <div className="nd-stagger">

              {/* Meta */}
              <ReviewCard icon={<Briefcase size={16} />} label="Mission">
                <ReviewField label="Titre" value={parsed.title} onChange={v => setParsed(p => ({ ...p, title: v }))} required />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                  <ReviewField label="Client" value={parsed.client} onChange={v => setParsed(p => ({ ...p, client: v }))} />
                  <ReviewField label="Secteur" value={parsed.sector} onChange={v => setParsed(p => ({ ...p, sector: v }))} />
                </div>
              </ReviewCard>

              <ReviewCard icon={<Globe size={16} />} label="Cadre">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                  <ReviewField label="Lieu" value={parsed.location} onChange={v => setParsed(p => ({ ...p, location: v }))} />
                  <ReviewField label="Durée" value={parsed.duration} onChange={v => setParsed(p => ({ ...p, duration: v }))} />
                  <ReviewField label="Années d'exp. min" type="number" value={parsed.required_experience_years} onChange={v => setParsed(p => ({ ...p, required_experience_years: v ? parseInt(v) : null }))} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                  <ReviewField label="Date limite (AAAA-MM-JJ)" value={parsed.deadline} onChange={v => setParsed(p => ({ ...p, deadline: v }))} />
                  <ReviewField label="Début (AAAA-MM-JJ)" value={parsed.start_date} onChange={v => setParsed(p => ({ ...p, start_date: v }))} />
                </div>
              </ReviewCard>

              {/* Summary */}
              <ReviewCard icon={<FileText size={16} />} label="Résumé de mission">
                <textarea
                  value={parsed.mission_summary || ''}
                  onChange={(e) => setParsed(p => ({ ...p, mission_summary: e.target.value }))}
                  rows={3}
                  style={{
                    width: '100%', resize: 'vertical',
                    background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6,
                    padding: '8px 10px', color: 'var(--white)', fontSize: 12.5,
                    outline: 'none', fontFamily: 'inherit',
                  }}
                />
              </ReviewCard>

              {/* Skills editor */}
              <ReviewCard icon={<Sparkles size={16} />} label="Compétences requises" count={parsed.required_skills?.length || 0}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {(parsed.required_skills || []).map((s, i) => (
                    <div key={i} style={{
                      display: 'grid', gridTemplateColumns: '1fr 130px 110px 28px', gap: 6, alignItems: 'center',
                    }}>
                      <input
                        value={s.name}
                        onChange={(e) => updateSkill(i, 'name', e.target.value)}
                        placeholder="Nom de la compétence"
                        style={{
                          background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6,
                          padding: '7px 10px', color: 'var(--white)', fontSize: 12.5,
                          outline: 'none', fontFamily: 'inherit',
                        }}
                      />
                      <select
                        value={s.category || 'Technique'}
                        onChange={(e) => updateSkill(i, 'category', e.target.value)}
                        style={{
                          background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6,
                          padding: '7px 8px', color: 'var(--white)', fontSize: 12, fontFamily: 'inherit',
                        }}
                      >
                        {['Technique', 'Fonctionnel', 'Métier', 'Méthodologie', 'Outils', 'Langues', 'Soft skills', 'Autre'].map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <select
                        value={s.importance}
                        onChange={(e) => updateSkill(i, 'importance', e.target.value)}
                        style={{
                          background: s.importance === 'must_have' ? 'rgba(123,104,238,0.12)' : 'var(--bg)',
                          border: '1px solid var(--border)', borderRadius: 6,
                          padding: '7px 8px',
                          color: s.importance === 'must_have' ? 'var(--vio2)' : 'var(--muted2)',
                          fontSize: 11, fontWeight: 600, fontFamily: 'inherit',
                        }}
                      >
                        <option value="must_have">Obligatoire</option>
                        <option value="nice_to_have">Souhaitable</option>
                      </select>
                      <button onClick={() => removeSkill(i)} title="Retirer"
                        style={{
                          background: 'transparent', border: 'none', color: 'var(--muted)',
                          cursor: 'pointer', padding: 4, borderRadius: 4,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                  <button onClick={addSkill}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '7px 12px', marginTop: 4,
                      background: 'transparent', border: '1px dashed var(--border)',
                      borderRadius: 6, color: 'var(--muted2)',
                      fontSize: 12, fontWeight: 500, cursor: 'pointer',
                      fontFamily: 'inherit', width: 'fit-content',
                    }}>
                    <Plus size={12} /> Ajouter une compétence
                  </button>
                </div>
              </ReviewCard>

            </div>

            {/* CTA */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 12 }}>
              <button onClick={handleSave} disabled={saving}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '11px 22px', borderRadius: 9, border: 'none',
                  background: 'linear-gradient(135deg, var(--vio), var(--vio2))', color: '#fff',
                  fontSize: 14, fontWeight: 700,
                  cursor: saving ? 'wait' : 'pointer', fontFamily: 'inherit',
                  boxShadow: '0 6px 20px rgba(123,104,238,0.35)',
                  opacity: saving ? 0.7 : 1,
                }}>
                {saving ? <RefreshCw size={15} className="nd-spin" /> : <ArrowRight size={15} />}
                {saving ? 'Enregistrement…' : "Enregistrer l'AO et voir les correspondances"}
              </button>
              <button onClick={handleRetry} disabled={saving}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '10px 16px', borderRadius: 8,
                  border: '1px solid var(--border)', background: 'transparent',
                  color: 'var(--muted2)', fontSize: 13, fontWeight: 500,
                  cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                }}>
                <X size={13} /> Recommencer
              </button>
            </div>
          </>
        )}

        {/* ── ERROR ── */}
        {phase === PHASE.ERROR && (
          <div style={{
            ...cardBase,
            background: 'rgba(239,68,68,0.06)',
            border: '1px solid rgba(239,68,68,0.3)',
            textAlign: 'center', padding: '28px 26px',
          }}>
            <div style={{
              width: 52, height: 52, borderRadius: 14,
              background: 'rgba(239,68,68,0.15)', color: '#F87171',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 14px',
            }}><AlertCircle size={24} strokeWidth={1.8} /></div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--white)', marginBottom: 6 }}>
              L'analyse a échoué
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--muted2)', marginBottom: 18 }}>{errorMsg}</div>
            <button onClick={handleRetry}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '9px 18px', borderRadius: 8, border: 'none',
                background: 'var(--vio)', color: '#fff',
                fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              }}>
              <RefreshCw size={13} /> Réessayer
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes nd-pulse { 0% { transform: scale(0.6); opacity: 0.55; } 80% { transform: scale(1.6); opacity: 0; } 100% { transform: scale(1.6); opacity: 0; } }
        .nd-pulse-ring { position: absolute; width: 64px; height: 64px; border-radius: 50%; background: rgba(123,104,238,0.25); animation: nd-pulse 1.8s cubic-bezier(0.4,0,0.6,1) infinite; }
        .nd-pulse-ring-2 { animation-delay: 0.6s; }
        @keyframes nd-sparkle-rotate { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .nd-sparkle-rotate { animation: nd-sparkle-rotate 4s linear infinite; }
        @keyframes nd-check-pop { 0% { transform: scale(0); opacity: 0; } 55% { transform: scale(1.2); opacity: 1; } 100% { transform: scale(1); opacity: 1; } }
        .nd-check-pop { animation: nd-check-pop 0.5s cubic-bezier(0.34,1.56,0.64,1) backwards; }
        @keyframes nd-success-fade { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
        .nd-success-banner { animation: nd-success-fade 0.4s ease-out backwards; }
        @keyframes nd-stagger-in { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .nd-stagger > * { animation: nd-stagger-in 0.4s ease-out backwards; margin-bottom: 12px; }
        .nd-stagger > *:nth-child(1) { animation-delay: 0.10s; }
        .nd-stagger > *:nth-child(2) { animation-delay: 0.18s; }
        .nd-stagger > *:nth-child(3) { animation-delay: 0.26s; }
        .nd-stagger > *:nth-child(4) { animation-delay: 0.34s; }
        @keyframes nd-spin { to { transform: rotate(360deg); } }
        .nd-spin { animation: nd-spin 0.9s linear infinite; }
      `}</style>
    </AppLayout>
  );
};

// ── Helpers ────────────────────────────────────────────────────────────────
const normalizeParsed = (raw) => ({
  title: raw.title || '',
  client: raw.client || '',
  sector: raw.sector || '',
  location: raw.location || '',
  duration: raw.duration || '',
  languages: raw.languages || [],
  required_experience_years: raw.required_experience_years ?? null,
  mission_summary: raw.mission_summary || '',
  required_skills: Array.isArray(raw.required_skills) ? raw.required_skills : [],
  deadline: raw.deadline || '',
  start_date: raw.start_date || '',
  notes: raw.notes || '',
  raw_content: raw.raw_content || '',
  extracted: raw,
});

const ReviewCard = ({ icon, label, count, children }) => (
  <div style={{
    background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: 12, padding: '14px 16px',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <span style={{
          width: 26, height: 26, borderRadius: 7,
          background: 'rgba(123,104,238,0.12)', color: 'var(--vio2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>{icon}</span>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--muted2)' }}>{label}</span>
      </div>
      {typeof count === 'number' && (
        <span style={{
          fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 10,
          background: 'rgba(123,104,238,0.15)', color: 'var(--vio2)',
        }}>{count}</span>
      )}
    </div>
    <div>{children}</div>
  </div>
);

const ReviewField = ({ label, value, onChange, type = 'text', required = false }) => (
  <div>
    <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', marginBottom: 4, letterSpacing: 0.3, textTransform: 'uppercase' }}>
      {label}{required && <span style={{ color: '#F87171' }}> *</span>}
    </div>
    <input
      type={type}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: '100%', background: 'var(--bg)', border: '1px solid var(--border)',
        borderRadius: 6, padding: '7px 10px', color: 'var(--white)', fontSize: 12.5,
        outline: 'none', fontFamily: 'inherit',
      }}
    />
  </div>
);

export default NewTender;
